/**
 * src/components/public/blog/magazine/NewsletterCompactForm.tsx
 *
 * Magazine layout (YÖN 2) — sidebar içinde sade compact newsletter form.
 * Kart bg yok, sadece input + button. Card kapsayıcı parent'ta zaten yok.
 *
 * POST /api/newsletter — 201/202 success, 4xx/5xx error. (Endpoint Zod + rate
 * limit + idempotent insert; mail teyidi Phase β.)
 */

'use client'

import { useState, type FormEvent } from 'react'

interface NewsletterCompactFormProps {
  emailPlaceholder: string
  submitLabel: string
  submittingLabel: string
  successTitle: string
  successBody: string
  errorBody: string
}

export function NewsletterCompactForm({
  emailPlaceholder,
  submitLabel,
  submittingLabel,
  successTitle,
  successBody,
  errorBody,
}: NewsletterCompactFormProps) {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle')

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!email.trim()) return
    setStatus('sending')
    try {
      const res = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      })
      if (res.ok) {
        setStatus('success')
        setEmail('')
        return
      }
      setStatus('error')
    } catch {
      setStatus('error')
    }
  }

  if (status === 'success') {
    return (
      <div className="text-xs text-emerald-700 dark:text-emerald-400">
        <strong className="block mb-1 text-sm">{successTitle}</strong>
        {successBody}
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={emailPlaceholder}
        required
        disabled={status === 'sending'}
        className="w-full px-3 py-2 rounded-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-brand-500 disabled:opacity-60"
      />
      <button
        type="submit"
        disabled={status === 'sending'}
        className="w-full px-3 py-2 rounded-md bg-brand-600 hover:bg-brand-500 text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {status === 'sending' ? submittingLabel : submitLabel}
      </button>
      {status === 'error' && (
        <p className="text-xs text-rose-600 dark:text-rose-400" role="alert">
          {errorBody}
        </p>
      )}
    </form>
  )
}
